import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useBewerberList } from '../../api/bewerber';
import { useStadtList } from '../../api/stadt';
import { useExportBewerber } from '../../api/admin';
import { BewerberStatusBadge } from '../../components/bewerber/BewerberStatusBadge';
import { BewerberActionDropdown } from '../../components/bewerber/actions';
import { KanbanBoard } from '../../components/bewerber/KanbanBoard';
import { BewerberStatus, QueryBewerberDto } from '../../types/bewerber';
import { useBewerberFilters } from '../../hooks/useBewerberFilters';
import { useDetailNavigation } from '../../hooks/useDetailNavigation';
import { formatOrtStellen, formatOrtStellenTooltip } from '../../utils/formatOrtStellen';
import { UserSelector } from '../../components/UserSelector';
import { UserAvatar } from '../../components/UserAvatar';
import { useAuth } from '../../context/AuthContext';

export function BewerberListPage() {
  const { user } = useAuth();
  const { filters, setFilter, clearFilters, hasActiveFilters } = useBewerberFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [page, setPage] = useState(1);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filters.search, filters.status, filters.ortStelle, filters.assignedUserId]);

  // Store current list URL for "back to list" navigation
  useEffect(() => {
    sessionStorage.setItem('bewerber-list-url', window.location.pathname + window.location.search);
  }, [filters]);

  // Build query for table view
  const query = useMemo<QueryBewerberDto>(
    () => ({
      page,
      limit: 20,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      search: filters.search || undefined,
      status: filters.status || undefined,
      ortStelle: filters.ortStelle || undefined,
      assignedUserId: filters.assignedUserId || undefined,
    }),
    [page, filters.search, filters.status, filters.ortStelle, filters.assignedUserId]
  );

  const { data, isLoading, error } = useBewerberList(query);
  const { data: staedte } = useStadtList(true);
  const exportMutation = useExportBewerber();

  const handleExport = async () => {
    try {
      await exportMutation.mutateAsync({
        status: filters.status || undefined,
        search: filters.search || undefined,
      });
      toast.success('Export gestartet');
    } catch {
      toast.error('Fehler beim Export');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilter('search', searchInput);
  };

  const handleStatusFilter = (status: BewerberStatus | '') => {
    setFilter('status', status);
  };

  const handleOrtStelleFilter = (ortStelle: string) => {
    setFilter('ortStelle', ortStelle);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden der Bewerber</p>
      </div>
    );
  }

  const istKanban = filters.view === 'kanban';

  return (
    // Im Kanban füllt die Seite ab lg den Viewport (100vh minus das p-6 des
    // Layouts); das Board bekommt den Rest und scrollt innen, damit seine
    // waagerechte Scrollbar sichtbar bleibt.
    <div className={istKanban ? 'flex flex-col lg:h-[calc(100vh-3rem)]' : undefined}>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6 shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">Bewerber</h1>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          {/* View Toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
            <button
              type="button"
              onClick={() => setFilter('view', 'table')}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filters.view === 'table'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Tabelle
            </button>
            <button
              type="button"
              onClick={() => setFilter('view', 'kanban')}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filters.view === 'kanban'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Kanban
            </button>
          </div>
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {exportMutation.isPending ? 'Exportiert...' : 'CSV Export'}
          </button>
          <Link
            to="/bewerber/new"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Neuer Bewerber
          </Link>
        </div>
      </div>

      {/* Filters - shown for both views */}
      <div className="bg-white shadow rounded-lg mb-6 shrink-0">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
            {/* Search */}
            <form onSubmit={handleSearch} className="sm:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Suche
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name, E-Mail, Stadt..."
                  className="flex-1 block w-full rounded-l-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-700 hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => handleStatusFilter(e.target.value as BewerberStatus | '')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Alle Status</option>
                <option value={BewerberStatus.NEU}>Neu</option>
                <option value={BewerberStatus.KEINE_ANLAGEN}>Keine Anlagen</option>
                <option value={BewerberStatus.ZU_KLAEREN}>Zu Klären</option>
                <option value={BewerberStatus.NICHT_GEEIGNET}>Nicht geeignet</option>
                <option value={BewerberStatus.GEEIGNET}>Geeignet</option>
                <option value={BewerberStatus.EINGELADEN}>Eingeladen</option>
                <option value={BewerberStatus.VORSTELLUNG}>Vorstellung</option>
                <option value={BewerberStatus.IM_POOL}>Im Pool</option>
                <option value={BewerberStatus.KENNENLERNGESPRAECH}>Kennenlerngespräch</option>
                <option value={BewerberStatus.ONBOARDING}>Einstellen</option>
                <option value={BewerberStatus.GEWONNEN}>Gewonnen</option>
                <option value={BewerberStatus.OFFBOARDING}>Offboarding</option>
                <option value={BewerberStatus.VERLOREN}>Verloren</option>
              </select>
            </div>

            {/* Beworben für Filter */}
            <div>
              <label htmlFor="ortStelle" className="block text-sm font-medium text-gray-700 mb-1">
                Beworben für
              </label>
              <select
                id="ortStelle"
                value={filters.ortStelle}
                onChange={(e) => handleOrtStelleFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Alle Städte</option>
                {staedte?.map((stadt) => (
                  <option key={stadt.id} value={stadt.id}>
                    {stadt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned User Filter */}
            <div>
              <label htmlFor="assignedUserId" className="block text-sm font-medium text-gray-700 mb-1">
                Zugewiesen an
              </label>
              <div className="flex gap-2">
                <UserSelector
                  id="assignedUserId"
                  value={filters.assignedUserId || null}
                  onChange={(v) => setFilter('assignedUserId', v ?? '')}
                  unassignedLabel="Alle Bearbeiter"
                  className="flex-1"
                />
                {user && (
                  <button
                    type="button"
                    onClick={() => setFilter('assignedUserId', filters.assignedUserId === user.id ? '' : user.id)}
                    title="Mir zugewiesen"
                    className={`inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md whitespace-nowrap ${
                      filters.assignedUserId === user.id
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Meine
                  </button>
                )}
              </div>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setSearchInput('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Filter zurücksetzen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Kanban View */}
      {istKanban && (
        <div className="flex-1 min-h-0">
          <KanbanBoard
            filters={{
              search: filters.search,
              status: filters.status,
              ortStelle: filters.ortStelle,
              assignedUserId: filters.assignedUserId,
            }}
          />
        </div>
      )}

      {/* Table View */}
      {!istKanban && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Laden...</p>
            </div>
          ) : data?.data.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Bewerber gefunden</h3>
              <p className="mt-1 text-sm text-gray-500">
                Erstellen Sie einen neuen Bewerber oder ändern Sie Ihre Suchkriterien.
              </p>
            </div>
          ) : (
            <TableContent
              data={data}
              handlePageChange={handlePageChange}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Extracted table content component to keep main component cleaner
function TableContent({
  data,
  handlePageChange,
}: {
  data: any;
  handlePageChange: (page: number) => void;
}) {
  const detailProps = useDetailNavigation();

  return (
    <>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Kontakt
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Wohnort
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Beworben für
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Zugewiesen
            </th>
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data?.data.map((bewerber: any) => (
            <tr
              key={bewerber.id}
              className={`cursor-pointer ${bewerber.isMarked ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
              {...detailProps(`/bewerber/${bewerber.id}`)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-medium text-sm">
                        {bewerber.firstName[0]}{bewerber.lastName[0]}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {bewerber.firstName} {bewerber.lastName}
                      </span>
                      {bewerber.isMarked && (
                        <span
                          className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                          title="Markiert"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M4 4v17l6-3 6 3V4H4zm0-2h12a2 2 0 012 2v17.586a1 1 0 01-1.707.707L10 18.586l-6.293 3.707A1 1 0 012 21.586V4a2 2 0 012-2z" />
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      Zuletzt geändert: {new Date(bewerber.updatedAt).toLocaleDateString('de-DE')}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{bewerber.email || '-'}</div>
                <div className="text-sm text-gray-500">{bewerber.phone || '-'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{bewerber.city?.name || '-'}</div>
                <div className="text-sm text-gray-500">{bewerber.postalCode || ''}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div
                  className="text-sm text-gray-900"
                  title={formatOrtStellenTooltip(bewerber.ortStellen)}
                >
                  {formatOrtStellen(bewerber.ortStellen, { max: 2 }) || '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <BewerberStatusBadge status={bewerber.status} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {bewerber.assignedTo ? (
                  <div className="flex items-center gap-2">
                    <UserAvatar user={bewerber.assignedTo} size="sm" />
                    <span className="text-sm text-gray-700">
                      {bewerber.assignedTo.firstName} {bewerber.assignedTo.lastName}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">–</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                <BewerberActionDropdown bewerber={bewerber} align="right" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {data?.meta && data.meta.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(data.meta!.page - 1)}
              disabled={data.meta!.page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Zurück
            </button>
            <button
              onClick={() => handlePageChange(data.meta!.page + 1)}
              disabled={data.meta!.page >= data.meta!.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Weiter
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Zeige{' '}
                <span className="font-medium">{(data.meta.page - 1) * data.meta.limit + 1}</span>
                {' '}-{' '}
                <span className="font-medium">
                  {Math.min(data.meta.page * data.meta.limit, data.meta.total)}
                </span>
                {' '}von{' '}
                <span className="font-medium">{data.meta.total}</span> Ergebnissen
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(data.meta!.page - 1)}
                  disabled={data.meta!.page <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Zurück</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Seite {data.meta.page} von {data.meta.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(data.meta!.page + 1)}
                  disabled={data.meta!.page >= data.meta!.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Weiter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
